import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Logo";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { api } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/* Screen 8 — Session History (real sessions from backend). */

const initialsOf = (name) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
};

/* Duration formatter: M:SS for < 1h, H:MM:SS for ≥ 1h. */
const formatDuration = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${m}:${String(ss).padStart(2, "0")}`;
};

export default function History() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { session_id, partner_name }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/sessions")
      .then((r) => alive && setSessions(Array.isArray(r.data) ? r.data : []))
      .catch(() => alive && setSessions([]));
    return () => {
      alive = false;
    };
  }, []);

  const seedDemoSession = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const res = await api.post("/sessions/complete", {
        duration_seconds: 183,
        partner_name: "Jordan",
      });
      const sid = res?.data?.session_id;
      if (sid) navigate(`/review/${sid}`);
    } catch {
      toast.error("Could not create a demo session.");
      setSeeding(false);
    }
  };

  const handleView = (session_id) => navigate(`/review/${session_id}`);

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/sessions/${pendingDelete.session_id}`);
      setSessions((cur) => (cur || []).filter((s) => s.session_id !== pendingDelete.session_id));
      toast.success("Session deleted.");
      setPendingDelete(null);
    } catch {
      toast.error("Could not delete this session. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const loading = sessions === null;
  const list = sessions || [];

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] font-body text-white">
      <DashboardSidebar active="history" />

      <main className="flex-1 px-6 py-7 md:px-12 md:py-10" data-testid="history-page">
        {/* mobile logo bar */}
        <div className="mb-8 flex items-center justify-between md:hidden">
          <Logo className="text-2xl" />
        </div>

        <div className="mx-auto max-w-3xl">
          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <h1 className="font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">
              Practice History
            </h1>
          </div>

          {/* List */}
          <div className="mt-6 space-y-3">
            {loading ? (
              <div
                data-testid="history-loading"
                className="rounded-2xl border border-white/10 bg-[#141414] py-16 text-center text-sm text-zinc-500"
              >
                Loading sessions…
              </div>
            ) : list.length === 0 ? (
              <div
                data-testid="history-empty"
                className="rounded-2xl border border-white/10 bg-[#141414] p-10 text-center"
              >
                <p className="text-sm text-zinc-400">
                  No sessions yet. Complete a mock call to see your review here.
                </p>
                <button
                  data-testid="seed-demo-btn"
                  onClick={seedDemoSession}
                  disabled={seeding}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#E52525] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#c81f1f] disabled:opacity-50"
                >
                  <Sparkles size={16} />
                  {seeding ? "Creating…" : "Generate Demo Session"}
                </button>
              </div>
            ) : (
              list.map((s, i) => {
                return (
                  <motion.div
                    key={s.session_id}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    data-testid={`session-card-${s.session_id}`}
                    className="flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-[#141414] p-4 text-left transition-[border-color] duration-200 hover:border-white/20"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/5 text-sm font-bold text-zinc-200">
                      {initialsOf(s.partner_name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-white">
                        {s.partner_name || "Practice Partner"}
                      </p>
                      <p className="mt-0.5 text-sm text-zinc-400">
                        {formatDate(s.created_at)}
                      </p>
                    </div>
                    <span
                      data-testid={`session-duration-${s.session_id}`}
                      className="hidden shrink-0 text-sm font-medium text-zinc-300 sm:block"
                    >
                      {formatDuration(s.duration_seconds)}
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        data-testid={`view-${s.session_id}`}
                        onClick={() => handleView(s.session_id)}
                        title="View"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors duration-200 hover:bg-white/5 hover:text-white"
                      >
                        <Eye size={17} />
                      </button>
                      <button
                        type="button"
                        data-testid={`delete-${s.session_id}`}
                        onClick={() =>
                          setPendingDelete({
                            session_id: s.session_id,
                            partner_name: s.partner_name,
                          })
                        }
                        title="Delete session"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors duration-200 hover:bg-[#E52525]/10 hover:text-[#E52525]"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </main>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}
      >
        <AlertDialogContent
          data-testid="delete-dialog"
          className="border-white/10 bg-[#141414] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this session?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This permanently deletes the session with{" "}
              <span className="font-semibold text-zinc-200">
                {pendingDelete?.partner_name || "your partner"}
              </span>
              , its recording, and its transcript. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel"
              disabled={deleting}
              className="border-white/10 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-[#E52525] text-white hover:bg-[#c81f1f]"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
